import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useTransition} from 'react';
import {openDB, type DBSchema, type IDBPDatabase} from 'idb';
import {ensureTrailingSlash} from '../utils/resources';
import {useDocsetManifest} from './DocsetContext';
import type {SearchResult, SearchStatus} from '../types/search';
import type {DocsetManifest} from '../types/docset';

type StoredChunk = {
  hash: string;
  data: Uint8Array;
};

interface SearchDBSchema extends DBSchema {
  chunks: {
    key: string;
    value: StoredChunk;
  };
  meta: {
    key: string;
    value: string;
  };
}

type SearchContextValue = {
  status: SearchStatus;
  error: Error | null;
  query: string;
  setQuery: (value: string) => void;
  results: SearchResult[];
  ensureReady: () => Promise<void>;
};

type SqlJsDatabase = {
  close: () => void;
  prepare: (sql: string) => SqlJsStatement;
};

type SqlJsStatement = {
  bind: (params?: unknown[]) => void;
  step: () => boolean;
  getAsObject: () => Record<string, unknown>;
  free: () => void;
};

type SqlJsStatic = {
  Database: new (data?: Uint8Array) => SqlJsDatabase;
};

type DocsetSearchConfig = NonNullable<DocsetManifest['search']>;

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export function SearchProvider({children}: {children: React.ReactNode}) {
  const manifest = useDocsetManifest();
  const [status, setStatus] = useState<SearchStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isPending, startTransition] = useTransition();
  const [databases, setDatabases] = useState<SqlJsDatabase[]>([]);
  const readyPromise = useRef<Promise<void> | null>(null);
  const abortSearch = useRef<AbortController | null>(null);

  useEffect(() => {
    setQuery('');
    setResults([]);
    setStatus('idle');
    setError(null);
    readyPromise.current = null;
    abortSearch.current?.abort();
    abortSearch.current = null;
    databases.forEach((db) => db.close());
    setDatabases([]);
  }, [manifest.version]);

  const ensureReady = useCallback(async () => {
    if (status === 'ready' || status === 'loading') {
      return readyPromise.current ?? Promise.resolve();
    }
    if (!manifest.search) {
      const err = new Error('Search is not available for this documentation set.');
      setStatus('error');
      setError(err);
      throw err;
    }

    const basePath = ensureTrailingSlash(manifest.basePath);
    const searchInfo = manifest.search;

    const loadPromise = (async () => {
      setStatus('loading');
      try {
        const SQL = await loadSqlJs(`${basePath}${searchInfo.sql.script}`, `${basePath}${searchInfo.sql.wasm}`);

        const chunkBuffers = await loadSearchChunks(manifest.id, searchInfo, basePath);

        databases.forEach((db) => db.close());
        const dbInstances = chunkBuffers.map((buffer) => new SQL.Database(buffer));
        setDatabases(dbInstances);
        setStatus('ready');
        setError(null);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setStatus('error');
        throw error;
      }
    })();

    readyPromise.current = loadPromise.finally(() => {
      readyPromise.current = null;
    });
    return readyPromise.current;
  }, [manifest, databases, status]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      if (abortSearch.current) {
        abortSearch.current.abort();
        abortSearch.current = null;
      }
      return;
    }

    const controller = new AbortController();
    abortSearch.current = controller;

    const handle = window.setTimeout(async () => {
      try {
        await ensureReady();
        if (controller.signal.aborted) {
          return;
        }
        const run = () => {
          const hits = runSearch(databases, query.trim());
          if (!controller.signal.aborted) {
            startTransition(() => setResults(hits));
          }
        };
        if (typeof (window as any).requestIdleCallback === 'function') {
          (window as any).requestIdleCallback(run, {timeout: 300});
        } else {
          window.setTimeout(run, 0);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          const error = err instanceof Error ? err : new Error(String(err));
          setError(error);
          setStatus('error');
        }
      }
    }, 200);

    return () => {
      controller.abort();
      window.clearTimeout(handle);
      abortSearch.current = null;
    };
  }, [query, ensureReady, databases]);

  useEffect(() => {
    return () => {
      databases.forEach((db) => db.close());
    };
  }, [databases]);

  const value = useMemo<SearchContextValue>(
    () => ({
      status,
      error,
      query,
      setQuery,
      results,
      ensureReady,
    }),
    [status, error, query, results, ensureReady],
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

export function useSearch(): SearchContextValue {
  const ctx = useContext(SearchContext);
  if (!ctx) {
    throw new Error('useSearch must be used within SearchProvider');
  }
  return ctx;
}

async function loadSearchChunks(docsetId: string, searchInfo: DocsetSearchConfig, basePath: string): Promise<Uint8Array[]> {
  const manifestResponse = await fetch(`${basePath}${searchInfo.manifest}`, {cache: 'no-store'});
  if (!manifestResponse.ok) {
    throw new Error(`Unable to load search manifest (${manifestResponse.status})`);
  }
  const manifestJson = (await manifestResponse.json()) as {
    version: string;
    totalRecords: number;
    resources: Array<{chunk: string; size: number; hash: string}>;
  };

  const db = await openDB<SearchDBSchema>(`docset-search-${docsetId}`, 1, {
    upgrade(database: IDBPDatabase<SearchDBSchema>) {
      if (!database.objectStoreNames.contains('chunks')) {
        database.createObjectStore('chunks');
      }
      if (!database.objectStoreNames.contains('meta')) {
        database.createObjectStore('meta');
      }
    },
  });

  const storedVersion = await db.get('meta', 'version');
  if (storedVersion !== manifestJson.version) {
    await db.clear('chunks');
    await db.put('meta', manifestJson.version, 'version');
  }

  const buffers: Uint8Array[] = [];

  for (const resource of manifestJson.resources) {
    const cached = await db.get('chunks', resource.chunk);
    if (cached && cached.hash === resource.hash) {
      buffers.push(cached.data);
      continue;
    }

    const response = await fetch(`${basePath}search/${resource.chunk}?v=${manifestJson.version}&h=${resource.hash}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Failed to download search chunk ${resource.chunk} (${response.status})`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);
    await db.put('chunks', {hash: resource.hash, data}, resource.chunk);
    buffers.push(data);
  }

  return buffers;
}

async function loadSqlJs(scriptUrl: string, wasmUrl: string): Promise<SqlJsStatic> {
  if (typeof window === 'undefined') {
    throw new Error('SQL.js is only available in the browser.');
  }

  if (!(window as any).initSqlJs) {
    await loadScript(scriptUrl);
  }

  const initSqlJs = (window as any).initSqlJs;
  if (typeof initSqlJs !== 'function') {
    throw new Error('SQL.js loader not found.');
  }

  return initSqlJs({
    locateFile(file: string) {
      if (file.endsWith('.wasm')) {
        return wasmUrl;
      }
      return scriptUrl;
    },
  });
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-sqljs="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve(), {once: true});
        existing.addEventListener('error', () => reject(new Error(`Failed to load script ${src}`)), {once: true});
      }
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.sqljs = src;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', () => reject(new Error(`Failed to load script ${src}`)));
    document.head.appendChild(script);
  });
}

function runSearch(databases: SqlJsDatabase[], query: string): SearchResult[] {
  if (!query || databases.length === 0) {
    return [];
  }

  const normalized = query.replace(/\s+/g, ' ').trim();
  const hits: Array<{score: number; result: SearchResult}> = [];

  for (const db of databases) {
    const stmt = db.prepare(
      `SELECT path, page_title as pageTitle, heading_title as headingTitle, bm25(entries) as score
       FROM entries
       WHERE entries MATCH ?
       ORDER BY bm25(entries)
       LIMIT 12`,
    );
    try {
      stmt.bind([normalized]);
      while (stmt.step()) {
        const record = stmt.getAsObject();
        hits.push({
          score: typeof record.score === 'number' ? (record.score as number) : 0,
          result: {
            path: String(record.path ?? ''),
            pageTitle: String(record.pageTitle ?? ''),
            headingTitle: record.headingTitle ? String(record.headingTitle) : null,
          },
        });
      }
    } finally {
      stmt.free();
    }
  }

  hits.sort((a, b) => a.score - b.score);
  const unique = new Map<string, SearchResult>();
  for (const hit of hits) {
    if (!unique.has(hit.result.path)) {
      unique.set(hit.result.path, hit.result);
    }
    if (unique.size >= 30) {
      break;
    }
  }

  return Array.from(unique.values()).slice(0, 12);
}
